import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatWidget from '../components/ChatWidget';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiTrendingUp, FiDollarSign, FiCheckCircle, FiArrowRight, FiX, FiUser, FiHeart, FiHome, FiClock, FiTarget, FiUsers } = FiIcons;

const WholeLifePage = () => {
  const benefits = [
    'Guaranteed approval for ages 66-85',
    'Lifetime coverage that never expires',
    'Cash value grows tax-deferred',
    'Fixed premiums that never increase',
    'No medical exam required',
    'Policy loans available against cash value'
  ];

  const whoShouldConsider = [
    {
      icon: FiUser,
      title: 'Seniors (Ages 66-85)',
      description: 'Guaranteed acceptance with no medical questions',
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      scenarios: [
        'Want to leave inheritance',
        'Cover final expenses',
        'No term life options available'
      ]
    },
    {
      icon: FiHeart,
      title: 'Estate Planners',
      description: 'Tax-advantaged wealth transfer to heirs',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      scenarios: [
        'High net worth individuals',
        'Tax planning strategies',
        'Legacy preservation'
      ]
    },
    {
      icon: FiHome,
      title: 'Conservative Investors',
      description: 'Stable, guaranteed returns with cash value',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      scenarios: [
        'Risk-averse savers',
        'Diversification strategy',
        'Guaranteed growth'
      ]
    },
    {
      icon: FiTarget,
      title: 'Business Owners',
      description: 'Key person insurance and succession planning',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      scenarios: [
        'Business succession',
        'Key person coverage',
        'Buy-sell agreements'
      ]
    }
  ];

  const whoShouldNotConsider = [
    {
      icon: FiClock,
      title: 'Young Adults (20s-30s)',
      description: 'Term life offers much more coverage for less cost',
      reason: 'Better Options Available',
      alternative: 'Consider 20-30 year term life insurance'
    },
    {
      icon: FiDollarSign,
      title: 'Budget-Conscious Families',
      description: 'Need maximum coverage at lowest cost',
      reason: 'Cost vs Coverage',
      alternative: 'Term life provides 5-10x more coverage'
    },
    {
      icon: FiTrendingUp,
      title: 'Investment Seekers',
      description: 'Looking for high returns on investment',
      reason: 'Low Returns',
      alternative: 'Invest difference in market for better returns'
    },
    {
      icon: FiUsers,
      title: 'Temporary Needs',
      description: 'Only need coverage for specific time period',
      reason: 'Temporary Coverage',
      alternative: 'Term life matches temporary needs perfectly'
    }
  ];

  const testimonials = [
    {
      name: 'Robert M.',
      age: 68,
      location: 'Sacramento, CA',
      quote: 'I thought I was too old for life insurance, but the guaranteed approval whole life policy was perfect. Now I know my wife will be taken care of.',
      coverage: '$25,000 Whole Life'
    },
    {
      name: 'Dorothy S.',
      age: 74,
      location: 'Fresno, CA',
      quote: 'The cash value feature is wonderful. I can borrow against my policy if I need extra money, and my grandchildren will still inherit the death benefit.',
      coverage: '$15,000 Whole Life'
    },
    {
      name: 'Frank L.',
      age: 71,
      location: 'Riverside, CA',
      quote: 'Simple application, instant approval, and peace of mind. This policy will help cover my final expenses and leave something for my children.',
      coverage: '$20,000 Whole Life'
    }
  ];

  const cashValueExample = [
    { year: 1, premium: 600, cashValue: 0, deathBenefit: 15000 },
    { year: 5, premium: 3000, cashValue: 1200, deathBenefit: 15000 },
    { year: 10, premium: 6000, cashValue: 3500, deathBenefit: 15000 },
    { year: 15, premium: 9000, cashValue: 6200, deathBenefit: 15000 },
    { year: 20, premium: 12000, cashValue: 9800, deathBenefit: 15000 }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-green-600 to-green-800 pt-24 pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center mb-4">
                <SafeIcon icon={FiShield} className="h-8 w-8 mr-3" />
                <span className="font-semibold">Whole Life Insurance</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Lifetime Protection with Guaranteed Approval
              </h1>
              <p className="text-xl text-green-100 mb-8">
                Perfect for seniors ages 66-85. Build cash value while ensuring your loved ones are protected. No medical exam required - guaranteed acceptance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://quote.burbankinsuranceservices.com/Dt2tCn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
                >
                  Get My Price
                  <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-lg p-8 text-secondary-900"
            >
              <h3 className="text-2xl font-bold mb-6">Guaranteed Issue Features</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="font-medium">Age Range</span>
                  <span className="text-green-600 font-bold">66-85 years</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="font-medium">Coverage</span>
                  <span className="text-green-600 font-bold">$1,000-$30,000</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="font-medium">Medical Exam</span>
                  <span className="text-green-600 font-bold">Not Required</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                  <span className="font-medium">Approval</span>
                  <span className="text-green-600 font-bold">Guaranteed</span>
                </div>
              </div>
              <p className="text-sm text-secondary-600 mt-4">
                Coverage starts immediately after first payment
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who Should Consider Whole Life - Infographic */}
      <section className="py-16 bg-gradient-to-br from-secondary-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
              Is Whole Life Insurance Right for You?
            </h2>
            <p className="text-lg text-secondary-600 max-w-3xl mx-auto">
              Whole life insurance isn't for everyone. Here's who should consider it and who has better alternatives.
            </p>
          </motion.div>

          {/* Who SHOULD Consider */}
          <div className="mb-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-green-700 mb-2">✅ Perfect Candidates for Whole Life</h3>
              <p className="text-secondary-600">These situations make whole life insurance an excellent choice</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className={`${candidate.bgColor} ${candidate.borderColor} border-2 rounded-xl p-6 hover:shadow-lg transition-shadow`}
                >
                  <div className="text-center mb-4">
                    <div className={`${candidate.bgColor} rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border ${candidate.borderColor}`}>
                      <SafeIcon icon={candidate.icon} className={`h-8 w-8 ${candidate.color}`} />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-4">{candidate.description}</p>
                  </div>
                  <div>
                    <h5 className="font-semibold text-secondary-800 mb-2 text-sm">Best for:</h5>
                    <ul className="space-y-1">
                      {candidate.scenarios.map((scenario, idx) => (
                        <li key={idx} className="text-xs text-secondary-700 flex items-center">
                          <SafeIcon icon={FiCheckCircle} className={`h-3 w-3 ${candidate.color} mr-2`} />
                          {scenario}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Who Should NOT Consider */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-8"
            >
              <h3 className="text-2xl font-bold text-red-700 mb-2">❌ Better Alternatives Available</h3>
              <p className="text-secondary-600">These situations typically have better, more affordable options</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {whoShouldNotConsider.map((candidate, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-red-50 border-2 border-red-200 rounded-xl p-6 hover:shadow-lg transition-shadow"
                >
                  <div className="text-center mb-4">
                    <div className="bg-red-100 rounded-full p-3 w-16 h-16 mx-auto mb-3 flex items-center justify-center border border-red-200">
                      <SafeIcon icon={candidate.icon} className="h-8 w-8 text-red-600" />
                    </div>
                    <h4 className="font-bold text-secondary-900 mb-2">{candidate.title}</h4>
                    <p className="text-sm text-secondary-600 mb-3">{candidate.description}</p>
                  </div>
                  <div className="space-y-2">
                    <div className="bg-red-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-red-800">Why Not:</p>
                      <p className="text-xs text-red-700">{candidate.reason}</p>
                    </div>
                    <div className="bg-green-100 rounded-lg p-2">
                      <p className="text-xs font-semibold text-green-800">Better Option:</p>
                      <p className="text-xs text-green-700">{candidate.alternative}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Decision Helper */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-12 bg-white rounded-xl shadow-lg p-8"
          >
            <h3 className="text-2xl font-bold text-secondary-900 text-center mb-8">
              Quick Decision Guide
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="text-center">
                <div className="bg-green-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiCheckCircle} className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <h4 className="font-bold text-green-800 mb-2">Choose Whole Life If:</h4>
                  <ul className="text-sm text-green-700 space-y-1 text-left">
                    <li>• You're 66+ years old</li>
                    <li>• You want guaranteed acceptance</li>
                    <li>• You need permanent coverage</li>
                    <li>• You want cash value growth</li>
                    <li>• Estate planning is a priority</li>
                  </ul>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-blue-100 rounded-lg p-6 mb-4">
                  <SafeIcon icon={FiClock} className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-bold text-blue-800 mb-2">Choose Term Life If:</h4>
                  <ul className="text-sm text-blue-700 space-y-1 text-left">
                    <li>• You're under 65 years old</li>
                    <li>• You need maximum coverage</li>
                    <li>• You have temporary needs</li>
                    <li>• You want lowest premiums</li>
                    <li>• You have young dependents</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-secondary-900 mb-6">
                Why Choose Whole Life Insurance?
              </h2>
              <p className="text-lg text-secondary-600 mb-8">
                Unlike term life insurance, whole life provides permanent protection that lasts your entire lifetime, plus builds cash value you can access during your lifetime.
              </p>
              <ul className="space-y-4">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start">
                    <SafeIcon icon={FiCheckCircle} className="h-5 w-5 text-green-500 mr-3 mt-1" />
                    <span className="text-secondary-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-secondary-50 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-secondary-900 mb-6">Cash Value Growth Example</h3>
              <p className="text-sm text-secondary-600 mb-4">$15,000 policy with $50/month premium</p>
              <div className="space-y-3">
                {cashValueExample.map((row, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-secondary-200">
                    <span className="font-medium">Year {row.year}</span>
                    <div className="text-right">
                      <div className="text-sm text-secondary-600">Cash Value: ${row.cashValue.toLocaleString()}</div>
                      <div className="text-sm font-medium text-green-600">Death Benefit: ${row.deathBenefit.toLocaleString()}</div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-secondary-500 mt-4">
                *Illustration purposes only. Actual values may vary.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            How Whole Life Insurance Works
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg p-6 text-center"
            >
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-green-600">1</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">Pay Premiums</h3>
              <p className="text-secondary-600">
                Pay fixed monthly premiums that never increase. Part goes to insurance, part builds cash value.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-lg p-6 text-center"
            >
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-green-600">2</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">Build Cash Value</h3>
              <p className="text-secondary-600">
                Your policy accumulates cash value over time that you can borrow against or withdraw.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-lg p-6 text-center"
            >
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <span className="text-2xl font-bold text-green-600">3</span>
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-3">Lifetime Protection</h3>
              <p className="text-secondary-600">
                Your beneficiaries receive the full death benefit when you pass away, regardless of age.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-secondary-900 text-center mb-12">
            What Our Whole Life Customers Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-secondary-50 rounded-lg p-6"
              >
                <blockquote className="text-secondary-700 mb-4 italic">
                  "{testimonial.quote}"
                </blockquote>
                <div className="border-t border-secondary-200 pt-4">
                  <p className="font-semibold text-secondary-900">{testimonial.name}</p>
                  <p className="text-sm text-secondary-600">{testimonial.location}</p>
                  <p className="text-sm text-green-600 font-medium mt-1">{testimonial.coverage}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Get Guaranteed Whole Life Insurance Today
          </h2>
          <p className="text-xl text-green-100 mb-8">
            Ages 66-85 automatically approved. No medical exam, no health questions.
          </p>
          <a
            href="https://quote.burbankinsuranceservices.com/Dt2tCn"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 transition-colors"
          >
            Get My Price
            <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
          </a>
          <p className="mt-4 text-sm text-green-200">
            Coverage starts immediately • Fixed premiums for life
          </p>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default WholeLifePage;